import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLogOut, FiShield, FiUser, FiExternalLink } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { clearAuthSession } from '../utils/auth';
import { clearMicrosoftTokens } from '../utils/microsoftAuth';

const AppHeader = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [provider, setProvider] = useState('microsoft');
  const [itsmLoading, setItsmLoading] = useState(false);

  useEffect(() => {
    const accessToken = localStorage.getItem('ms_access_token') || '';
    const googleToken = localStorage.getItem('google_access_token') || '';
    const storedProvider = localStorage.getItem('active_provider') || 'microsoft';
    const displayName = localStorage.getItem('ms_user_name') ||
                        (googleToken ? 'Gmail User' : accessToken ? 'Authenticated' : '');
    const role = localStorage.getItem('ms_user_role') || null;
    const email = localStorage.getItem('ms_user_email') || '';
    setIsAuthenticated(Boolean(accessToken || googleToken));
    setProvider(storedProvider);
    setUserDisplayName(displayName);
    setUserEmail(email);
    setUserRole(role);
  }, []);

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) return;
    clearMicrosoftTokens();
    clearAuthSession();
    toast.success('Signed out successfully');
    navigate('/');
    window.location.reload();
  };

  const handleOpenItsm = async () => {
    if (!userEmail || !userDisplayName) {
      toast.error('Please sign in first to open ITSM.');
      return;
    }
    setItsmLoading(true);
    try {
      const { data } = await axios.post('/api/itsm/redirect', {
        email: userEmail,
        name: userDisplayName,
      });
      if (data.redirectUrl) {
        window.open(data.redirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('ITSM redirect URL was not returned.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not open ITSM.');
    } finally {
      setItsmLoading(false);
    }
  };

  return (
    <div className="bg-white border-b border-neutral-200 shadow-soft">
      <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-10 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-accent-600 rounded-xl flex items-center justify-center shadow-medium">
              <FiMail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Bulk Mail Sender</h1>
              <p className="text-sm text-neutral-500">Professional email campaigns</p>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-success-50 border border-success-200 rounded-lg">
                  <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                  <span className="text-success-700 font-medium text-sm">
                    {userDisplayName || 'Authenticated'}
                  </span>
                </div>
                {userRole === 'admin' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-primary-50 border border-primary-200 text-primary-700 rounded-lg text-xs font-semibold">
                    <FiShield className="w-3.5 h-3.5" /> Admin
                  </span>
                )}
                {userRole === 'user' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-neutral-100 border border-neutral-200 text-neutral-600 rounded-lg text-xs font-semibold">
                    <FiUser className="w-3.5 h-3.5" /> User
                  </span>
                )}
              </div>
            )}

            <Link
              to="/history"
              className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
            >
              History
            </Link>
            <Link
              to="/inbox"
              className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
            >
              Inbox
            </Link>
            <Link
              to="/tracking"
              className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
            >
              Tracking
            </Link>
            <Link
              to="/contacts"
              className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
            >
              Contacts
            </Link>
            <Link
              to="/scheduled"
              className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
            >
              Scheduled
            </Link>
            <Link
              to="/whatsapp"
              className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
            >
              WhatsApp
            </Link>
            <button
              onClick={handleOpenItsm}
              disabled={itsmLoading || !isAuthenticated}
              className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 flex items-center gap-2 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title={isAuthenticated ? 'Open ITSM Support in a new tab' : 'Sign in to open Support'}
            >
              <FiExternalLink className="w-4 h-4" />
              {itsmLoading ? 'Opening…' : 'Support'}
            </button>

            {isAuthenticated ? (
              <button
                onClick={handleSignOut}
                className="px-4 py-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 flex items-center gap-2 font-medium text-sm"
                title={`Sign out from ${provider === 'gmail' ? 'Google' : 'Microsoft'}`}
              >
                <FiLogOut className="w-4 h-4" />
                Sign Out
              </button>
            ) : (
              <Link
                to="/"
                className="px-4 py-2 text-neutral-700 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-all duration-200 font-medium text-sm"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppHeader;
